import React, { useState, useEffect, useCallback, useRef } from 'react'
import PropTypes from 'prop-types'
import moment from 'moment'
import { debounce } from 'lodash'

// @material-ui
import { withWidth } from '@material-ui/core'
import { isWidthDown } from '@material-ui/core/withWidth'
import { darken } from '@material-ui/core/styles/colorManipulator'
import {
  red,
  blue,
  green,
  yellow,
  purple,
  orange,
} from '@material-ui/core/colors'

// core
import { useGlobal } from '_core/hooks/useGlobal'
import { withLang } from '_core/hocs/withLang'
import { getFormattedValue } from '_core/utils/formatPrice'
import { CurrencyChartView } from './CurrencyChartView'

const COLORS = [red, blue, green, yellow, purple, orange]

const LINE_COLORS = [
  ...COLORS.map(value => value[500]),
  ...COLORS.map(value => darken(value[500], 0.3)),
]

const RANGE_OPTIONS = [
  {
    id: 'day',
    title: 'r._.currencychart.text.rangeday',
    data: {
      interval: '1 hour',
      limit: 24,
    },
  },
  {
    id: 'week',
    title: 'r._.currencychart.text.rangeweek',
    data: {
      interval: '1 day',
      limit: 7,
    },
  },
  {
    id: 'month',
    title: 'r._.currencychart.text.rangemonth',
    data: {
      interval: '1 day',
      limit: 30,
    },
  },
]

const DEFAULT_MARGIN = 16

export const RawCurrencyChart = props => {
  const {
    width,
    rates,
    ranges,
    source,
    colors,
    currencies,
    initialRange,
    langInfo,
    render,
    Component,
  } = props

  const {
    request: { queue },
    config: { API_METHOD, PRICE_FORMAT },
  } = useGlobal()

  const [filter, setFilter] = useState({})
  const [nodeKey, setNodeKey] = useState(0)
  const [range, setRange] = useState(initialRange)
  const [isLoading, setIsLoading] = useState(false)
  const [yAxisWidth, setYAxisWidth] = useState(undefined)
  const selectLabelRef = useRef()
  const chartRef = useRef()

  useEffect(() => {
    setNodeKey(nk => nk + 1)
  }, [langInfo, source, range, ranges, rates, currencies])

  useEffect(() => {
    setFilter({})
  }, [source, rates, currencies])

  const updateMargin = useCallback(
    debounce(() => {
      if (chartRef.current == null) {
        setYAxisWidth(undefined)
        return
      }

      const ticks = chartRef.current.querySelectorAll(
        '.recharts-yAxis .recharts-cartesian-axis-tick-value'
      )

      const newMargin = [...ticks].reduce((result, tick) => {
        const width = tick.getBBox().width
        result < width && (result = width)
        return result
      }, 0)

      setYAxisWidth(Math.ceil(newMargin) + DEFAULT_MARGIN)
    }, 10),
    [chartRef.current]
  )

  const isXs = isWidthDown('xs', width)
  const canvas = document.createElement('canvas')
  const context = canvas.getContext('2d')
  const rangeData = ranges.find(item => `${item.id}` === `${range}`)

  const { fractionGroupSize, decimalSeparator } = PRICE_FORMAT
  const { scale } = currencies.find(item => `${item.id}` === `${source}`)
  const minScale = scale > fractionGroupSize ? fractionGroupSize : scale

  context.font =
    selectLabelRef.current != null
      ? window.getComputedStyle(selectLabelRef.current).getPropertyValue('font')
      : ''

  const codes = rates.reduce((result, rate) => {
    const { code } = currencies.find(item => `${rate.id}` === `${item.id}`)
    result[rate.id] = code
    return result
  }, {})

  const lineColors = rates.reduce((result, item, index) => {
    const count = Math.floor(index / colors.length)
    result[item.id] = colors[index - count * colors.length]
    return result
  }, {})

  const getTickScale = ({ valueScale, minScale }) =>
    valueScale > minScale ? valueScale : minScale

  const getValueScale = value => {
    const valueFract = `${value}`.split(decimalSeparator)[1]
    return valueFract != null ? valueFract.replace(/0*$/, '').length : 0
  }

  const formatTick = value => {
    updateMargin()

    if (!Number.isFinite(value)) {
      return value
    }

    const scale = getTickScale({ valueScale: getValueScale(value), minScale })
    return getFormattedValue(value, { scale })
  }

  const formatSequence = list =>
    list.map(item => ({
      time: moment(item.time.value).format(range === 'day' ? 'LT' : 'L'),

      ...item.currencies.reduce((accum, { id, value }) => {
        accum[id] = value
        return accum
      }, {}),
    }))

  const getSequence = async () => {
    setIsLoading(true)

    const result = await queue(
      API_METHOD.MONEY_CURRENCY_CHART,
      { source: source, ...rangeData.data },
      RawCurrencyChart.displayName
    )

    setIsLoading(false)
    return formatSequence(result.list)
  }

  const getOnFilterClick = id => () =>
    setFilter(val => ({ ...val, [id]: !val[id] }))

  const childProps = {
    isXs,
    codes,
    rates,
    range,
    ranges,
    source,
    filter,
    nodeKey,
    isLoading,
    yAxisWidth,
    lineColors,
    currencies,
    defaultMargin: DEFAULT_MARGIN,
    formatTick,
    getSequence,
    chartRef,
    selectLabelRef,
    getOnFilterClick,
    onSelectChange: event => setRange(event.target.value),
  }

  return render != null ? render(childProps) : <Component {...childProps} />
}

RawCurrencyChart.displayName = 'RawCurrencyChart'

RawCurrencyChart.defaultProps = {
  initialRange: 'month',
  ranges: RANGE_OPTIONS,
  colors: LINE_COLORS,
  Component: CurrencyChartView,
}

RawCurrencyChart.propTypes = {
  // self props
  render: PropTypes.func,
  Component: PropTypes.elementType,
  source: PropTypes.string,
  colors: PropTypes.arrayOf(PropTypes.string),
  initialRange: PropTypes.oneOf(RANGE_OPTIONS.map(item => item.id)),

  currencies: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.number,
      scale: PropTypes.number,
      code: PropTypes.string,
      title: PropTypes.string,
      format: PropTypes.string,
      user_visible: PropTypes.bool,
      switch_visible: PropTypes.bool,
    })
  ),

  rates: PropTypes.arrayOf(
    PropTypes.shape({
      time: PropTypes.object,
      value: PropTypes.number,
    })
  ),

  ranges: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string,
      title: PropTypes.string,
      data: PropTypes.shape({
        limit: PropTypes.number,
        interval: PropTypes.string,
      }),
    })
  ).isRequired,

  // `withWidth` HOC props
  width: PropTypes.string.isRequired,
}

export const CurrencyChart = withLang(withWidth()(RawCurrencyChart))
